import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import {
  useAssignSettingSelfServeBrandInstantAnswers,
  useSettingSelfServeBrandInstantAnswers,
} from "@faire/help-center-utils/settings/useSettingSelfServiceBrandInstantAnswers";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Synchronized } from "@faire/web--source/slate/icons/foundation/Synchronized";
import React from "react";

import { SelfServeNode, SelfServeNodes } from "../../types";

export const useIntegrationsBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const enableSettingSelfServeBrandInstantAnswers =
    useSettingSelfServeBrandInstantAnswers();
  const assignSettingSelfServeBrandInstantAnswers =
    useAssignSettingSelfServeBrandInstantAnswers();
  const articles = useArticles();

  const commonFields: SelfServeNode = {
    id: "Integrations",
    label: strictLocalize({
      defaultMessage: "Integrations",
      description: {
        text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
      },
    }),
    icon: <Synchronized fontSize="24px" color="faireBlack" />,
    articles: [
      articles.Integration.SOFTWARE_INTEGRATION_WITH_FAIRE,
      articles.Integration.ONBOARDING_WITH_SHOPIFY,
      articles.Integration.CAN_I_INTEGRATE_WITH_SHOPIFY,
    ],
    level: 1,
    assignSetting: assignSettingSelfServeBrandInstantAnswers,
    sidebarTitle: strictLocalize({
      defaultMessage: "Integration issue",
      description: {
        text: "Title for displaying the issue on the order summary sidebar",
      },
    }),
  };

  if (!enableSettingSelfServeBrandInstantAnswers) {
    return {
      Integrations: {
        ...commonFields,
      },
    };
  }

  return {
    Integrations: {
      ...commonFields,
      children: [
        "AboutIntegrations",
        "ProductImportWithShopify",
        "InventorySyncWithShopify",
        "PricingSyncWithShopify",
        "OrderSyncWithShopify",
        "SetUpIntegration",
        "ConnectToApi",
        "RequestIntegration",
      ],
    },
    AboutIntegrations: {
      id: "AboutIntegrations",
      label: strictLocalize({
        defaultMessage: "About integrations",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
    ProductImportWithShopify: {
      id: "ProductImportWithShopify",
      label: strictLocalize({
        defaultMessage: "Product import with Shopify",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
    InventorySyncWithShopify: {
      id: "InventorySyncWithShopify",
      label: strictLocalize({
        defaultMessage: "Inventory sync with Shopify",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
    PricingSyncWithShopify: {
      id: "PricingSyncWithShopify",
      label: strictLocalize({
        defaultMessage: "Pricing sync with Shopify",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
    OrderSyncWithShopify: {
      id: "OrderSyncWithShopify",
      label: strictLocalize({
        defaultMessage: "Order sync with Shopify",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
    SetUpIntegration: {
      id: "SetUpIntegration",
      label: strictLocalize({
        defaultMessage: "Set up integration",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
    ConnectToApi: {
      id: "ConnectToApi",
      label: strictLocalize({
        defaultMessage: "Connect to API",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
    RequestIntegration: {
      id: "RequestIntegration",
      label: strictLocalize({
        defaultMessage: "Request integration",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
    },
  };
};
